import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Space, Modal, Form, Input, Select, message, Tag, Popconfirm, Switch, Drawer, Typography, Checkbox } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HistoryOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../services/api';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useCanDelete } from '../hooks/useCanDelete';
import { trLower, trUpperNormalize } from '../utils/trLocale';

interface Account {
  id: number;
  accountCode: string;
  accountName: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  nationalId?: string;
  accountType: string;
  customerCategory?: string; // "Retail" veya "Wholesale"
  taxNumber?: string;
  taxOffice?: string;
  email?: string;
  phone?: string;
  address?: string;
  notificationConsent: boolean;
  isActive: boolean;
  balance?: number;
  lastPlate?: string;
  createdAt: string;
}

interface AccountType {
  value: number;
  name: string;
  displayName: string;
}

interface CustomerCategory {
  value: number;
  name: string;
  displayName: string;
}

const Accounts: React.FC = () => {
  const isMobile = useBreakpoint();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountTypes, setAccountTypes] = useState<AccountType[]>([]);
  const [customerCategories, setCustomerCategories] = useState<CustomerCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [form] = Form.useForm();
  const [selectedAccountType, setSelectedAccountType] = useState<number | null>(null);
  const [ledgerDrawerVisible, setLedgerDrawerVisible] = useState(false);
  const [ledgerAccountId, setLedgerAccountId] = useState<number | null>(null);
  const [ledgerData, setLedgerData] = useState<{ accountName: string; balance: number; entries: any[] } | null>(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const canDelete = useCanDelete();

  const filteredAccounts = useMemo(() => {
    if (!searchText.trim()) return accounts;
    const q = trLower(searchText.trim());
    const qDigits = q.replace(/\D/g, '');
    const filtered = accounts.filter((acc) => {
      const fullName = trLower([acc.firstName, acc.lastName].filter(Boolean).join(' '));
      const accountName = trLower(acc.accountName);
      const companyName = trLower(acc.companyName);
      const plate = trLower(acc.lastPlate);
      const phone = (acc.phone || '').replace(/\D/g, '');
      return (
        fullName.includes(q) ||
        accountName.includes(q) ||
        companyName.includes(q) ||
        plate.includes(q) ||
        (qDigits.length >= 3 && phone.includes(qDigits))
      );
    });
    const byId = new Map<number, Account>();
    filtered.forEach((acc) => byId.set(acc.id, acc));
    return Array.from(byId.values());
  }, [accounts, searchText]);

  useEffect(() => {
    loadAccounts();
    loadAccountTypes();
    loadCustomerCategories();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/accounts');
      const list = data || [];
      const byId = new Map<number, Account>();
      list.forEach((acc: Account) => byId.set(acc.id, acc));
      setAccounts(Array.from(byId.values()));
    } catch (error) {
      message.error('Cari hesaplar yüklenirken hata oluştu');
      console.error('Load accounts error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAccountTypes = async () => {
    try {
      const { data } = await api.get('/accounts/account-types');
      setAccountTypes(data);
    } catch (error) {
      message.error('Hesap tipleri yüklenirken hata oluştu');
      console.error('Load account types error:', error);
    }
  };

  const loadCustomerCategories = async () => {
    try {
      const { data } = await api.get('/accounts/customer-categories');
      setCustomerCategories(data);
    } catch (error) {
      message.error('Müşteri kategorileri yüklenirken hata oluştu');
      console.error('Load customer categories error:', error);
    }
  };

  const handleAdd = () => {
    setEditingAccount(null);
    form.resetFields();
    setSelectedAccountType(1); // Müşteri
    form.setFieldsValue({
      accountType: 1,
      customerCategory: 1, // Perakende
      notificationConsent: true,
    });
    setModalVisible(true);
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    // AccountType string'ini value'ya çevir
    const accountTypeValue = accountTypes.find(at => at.name === account.accountType)?.value;
    setSelectedAccountType(accountTypeValue || null);
    
    // CustomerCategory: API "Retail"/"Wholesale" string döner, form value (1/2) bekler
    // customerCategories henüz yüklenmemiş olabilir, bu yüzden direkt mapping kullan
    const customerCategoryValue = account.customerCategory
      ? (customerCategories.find(cc => cc.name === account.customerCategory)?.value ?? 
         (account.customerCategory === 'Retail' ? 1 : account.customerCategory === 'Wholesale' ? 2 : null))
      : null;
    
    form.setFieldsValue({
      ...account,
      accountType: accountTypeValue,
      customerCategory: customerCategoryValue,
      isActive: account.isActive
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await api.delete(`/accounts/${id}`);
      message.success('Cari hesap silindi');
      loadAccounts();
    } catch (error) {
      message.error('Cari hesap silinirken hata oluştu');
      console.error('Delete error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openLedgerDrawer = async (accountId: number) => {
    setLedgerAccountId(accountId);
    setLedgerDrawerVisible(true);
    setLedgerLoading(true);
    try {
      const [balanceRes, ledgerRes] = await Promise.all([
        api.get<{ accountId: number; accountName: string; balance: number }>(`/accounts/${accountId}/balance`),
        api.get<{ accountId: number; accountName: string; entries: any[]; balance: number }>(`/accounts/${accountId}/ledger`)
      ]);
      setLedgerData({
        accountName: ledgerRes.data.accountName,
        balance: ledgerRes.data.balance,
        entries: ledgerRes.data.entries || []
      });
    } catch (e) {
      message.error('Tahsilat geçmişi yüklenirken hata');
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const trUpper = (s: string | undefined) => (s != null && s !== '' ? String(s).toLocaleUpperCase('tr-TR') : s);
      const payload = {
        firstName: trUpper(values.firstName),
        lastName: trUpper(values.lastName),
        companyName: trUpper(values.companyName),
        accountType: parseInt(values.accountType),
        customerCategory: values.customerCategory != null ? parseInt(values.customerCategory) : null,
        nationalId: values.nationalId,
        taxNumber: values.taxNumber,
        taxOffice: values.taxOffice,
        email: values.email,
        phone: values.phone ? String(values.phone).replace(/\s/g, '') : undefined,
        address: values.address,
        notificationConsent: values.notificationConsent,
      };

      if (editingAccount) {
        await api.put(`/accounts/${editingAccount.id}`, {
          ...payload,
          isActive: values.isActive ?? true
        });
        message.success('Cari hesap güncellendi');
      } else {
        await api.post('/accounts', payload);
        message.success('Cari hesap eklendi');
      }
      setModalVisible(false);
      form.resetFields();
      loadAccounts();
    } catch (error) {
      message.error('Cari hesap kaydedilirken hata oluştu');
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<Account> = [
    {
      title: 'Hesap Kodu',
      dataIndex: 'accountCode',
      key: 'accountCode',
    },
    {
      title: 'Ad Soyad / Firma / Plaka',
      key: 'displayName',
      render: (_, record: Account) => {
        const hasPlate = Boolean(record.lastPlate?.trim());
        const personName = `${record.firstName} ${record.lastName}`.trim();
        const personWithPlate = hasPlate ? `${personName} - ${record.lastPlate!.trim()}` : personName;
        if (record.companyName) {
          return (
            <div>
              <div style={{ fontWeight: 'bold' }}>{record.companyName}</div>
              <div style={{ fontSize: '12px', color: '#888' }}>
                {personWithPlate}
              </div>
            </div>
          );
        }
        return personWithPlate;
      },
    },
    {
      title: 'Hesap Tipi',
      dataIndex: 'accountType',
      key: 'accountType',
      render: (type: string) => {
        const typeMap: { [key: string]: { color: string; label: string } } = {
          'Customer': { color: 'blue', label: 'Müşteri' },
          'Supplier': { color: 'green', label: 'Tedarikçi' },
          'Both': { color: 'purple', label: 'İkisi de' }
        };
        const typeInfo = typeMap[type] || { color: 'default', label: type };
        return <Tag color={typeInfo.color}>{typeInfo.label}</Tag>;
      },
    },
    {
      title: 'Müşteri Kategorisi',
      dataIndex: 'customerCategory',
      key: 'customerCategory',
      render: (category: string | undefined) => {
        if (!category) return '-';
        const categoryMap: { [key: string]: { color: string; label: string } } = {
          'Retail': { color: 'gold', label: '🏪 Perakende' },
          'Wholesale': { color: 'cyan', label: '📦 Toptan' }
        };
        const categoryInfo = categoryMap[category] || { color: 'default', label: category };
        return <Tag color={categoryInfo.color}>{categoryInfo.label}</Tag>;
      },
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string | undefined) => {
        if (!phone) return '-';
        const digits = phone.replace(/\D/g, '').slice(0, 10);
        if (!digits) return phone;
        return digits.replace(/(\d{3})(\d{0,3})(\d{0,2})(\d{0,2})/, (_: string, a: string, b: string, c: string, d: string) =>
          [a, b, c, d].filter(Boolean).join(' ')
        );
      },
    },
    {
      title: 'Bakiye (₺)',
      dataIndex: 'balance',
      key: 'balance',
      align: 'right',
      render: (_: unknown, record: Account) => {
        const v = record.balance ?? 0;
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        );
      }
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => openLedgerDrawer(record.id)}
            title="Tahsilat Geçmişi"
          />
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          {canDelete && (
            <Popconfirm
              title="Bu cari hesabı silmek istediğinizden emin misiniz?"
              onConfirm={() => handleDelete(record.id)}
              okText="Evet"
              cancelText="Hayır"
              placement="left"
            >
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Cari Hesaplar"
        extra={
          <Space wrap size="middle">
            <Input
              placeholder="Ad, soyad, firma, plaka veya cep telefonu ile ara"
              prefix={<SearchOutlined />}
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ minWidth: 200, maxWidth: 320 }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Yeni Cari Hesap
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredAccounts}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
          }}
        />
      </Card>

      <Modal
        title={editingAccount ? 'Cari Hesap Düzenle' : 'Yeni Cari Hesap'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {editingAccount && (
            <Form.Item
              name="accountCode"
              label="Hesap Kodu"
            >
              <Input disabled />
            </Form.Item>
          )}

          <Form.Item
            name="firstName"
            label="Ad"
            normalize={trUpperNormalize}
            rules={[{ required: true, message: 'Ad giriniz' }]}
          >
            <Input placeholder="Ad" />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Soyad"
            normalize={trUpperNormalize}
            rules={[{ required: true, message: 'Soyad giriniz' }]}
          >
            <Input placeholder="Soyad" />
          </Form.Item>

          <Form.Item
            name="companyName"
            label="Firma Adı (Opsiyonel)"
            normalize={trUpperNormalize}
          >
            <Input placeholder="Firma adı (varsa)" />
          </Form.Item>

          <Form.Item
            name="nationalId"
            label="TC Kimlik No (Opsiyonel)"
          >
            <Input placeholder="TC Kimlik No" maxLength={11} />
          </Form.Item>

          <Form.Item
            name="accountType"
            label="Hesap Tipi"
            rules={[{ required: true, message: 'Hesap tipi seçiniz' }]}
          >
            <Select 
              placeholder="Hesap tipi seçiniz"
              onChange={(value) => {
                setSelectedAccountType(value);
                // Müşteri değilse CustomerCategory'yi temizle
                if (value !== 1) { // 1 = Customer
                  form.setFieldsValue({ customerCategory: null });
                }
              }}
            >
              {accountTypes.map(type => (
                <Select.Option key={type.value} value={type.value}>
                  {type.displayName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Müşteri Kategorisi - Sadece Müşteri seçiliyse göster */}
          {selectedAccountType === 1 && (
            <Form.Item
              name="customerCategory"
              label="Müşteri Kategorisi"
              rules={[{ required: true, message: 'Müşteri kategorisi seçiniz' }]}
            >
              <Select placeholder="Müşteri kategorisi seçin">
                {customerCategories.map(cat => (
                  <Select.Option key={cat.value} value={cat.value}>
                    {cat.displayName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="taxNumber"
            label="Vergi Numarası"
          >
            <Input placeholder="Vergi numarası" />
          </Form.Item>

          <Form.Item
            name="taxOffice"
            label="Vergi Dairesi"
          >
            <Input placeholder="Vergi dairesi" />
          </Form.Item>

          <Form.Item
            name="email"
            label="E-posta"
            rules={[{ type: 'email', message: 'Geçerli bir e-posta adresi giriniz' }]}
          >
            <Input placeholder="E-posta adresi" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Telefon"
            normalize={(val) => {
              const digits = (val || '').replace(/\D/g, '').slice(0, 10);
              if (!digits) return '';
              return digits.replace(/(\d{3})(\d{0,3})(\d{0,2})(\d{0,2})/, (_: string, a: string, b: string, c: string, d: string) =>
                [a, b, c, d].filter(Boolean).join(' ')
              );
            }}
          >
            <Input placeholder="532 000 00 00" maxLength={14} />
          </Form.Item>

          <Form.Item
            name="address"
            label="Adres"
          >
            <Input.TextArea placeholder="Adres bilgisi" rows={3} />
          </Form.Item>

          <Form.Item
            name="notificationConsent"
            valuePropName="checked"
          >
            <Checkbox>Bildirim Onayı</Checkbox>
          </Form.Item>

          {editingAccount && (
            <Form.Item
              name="isActive"
              label="Hesap durumu"
              valuePropName="checked"
            >
              <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingAccount ? 'Güncelle' : 'Kaydet'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title="Tahsilat Geçmişi"
        placement="right"
        width={isMobile ? '100%' : 640}
        open={ledgerDrawerVisible}
        onClose={() => setLedgerDrawerVisible(false)}
      >
        {ledgerLoading && <div>Yükleniyor...</div>}
        {!ledgerLoading && ledgerData && (
          <>
            <Typography.Paragraph strong>{ledgerData.accountName}</Typography.Paragraph>
            <Typography.Paragraph>
              Cari bakiye: <Typography.Text type={ledgerData.balance >= 0 ? 'danger' : 'success'}>
                {ledgerData.balance.toFixed(2)} ₺
              </Typography.Text>
            </Typography.Paragraph>
            <Table
              size="small"
              dataSource={ledgerData.entries}
              rowKey="id"
              scroll={{ x: 400 }}
              columns={[
                { title: 'Tarih', dataIndex: 'entryDate', key: 'entryDate', render: (d: string) => dayjs(d).format('DD.MM.YYYY HH:mm'), width: 120 },
                { title: 'Tutar', dataIndex: 'amount', key: 'amount', render: (a: number) => `${a >= 0 ? '+' : ''}${a.toFixed(2)} ₺`, width: 100 },
                { title: 'Açıklama', dataIndex: 'description', key: 'description' },
                { title: 'Makbuz no', dataIndex: 'receiptNo', key: 'receiptNo', width: 100 }
              ]}
              pagination={{ pageSize: 15 }}
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

export default Accounts;


